/**
 * Step 5 — Confirmar e Enviar.
 *
 * Réplica do frame `web-step5-aceite`: o resumo repete, campo a campo, tudo o
 * que será submetido. Os dependentes removidos aparecem riscados em vez de
 * desaparecerem — o titular precisa ver que a remoção foi registrada.
 *
 * O checkbox da RN 561 não aparece no frame, mas é mantido porque o submit exige
 * `rn561_aceite: true` (spec §5): sem ele o envio não teria respaldo do aceite.
 *
 * O envio consome o token (spec §5). Se o POST falhar, o step continua na tela
 * com o erro visível para nova tentativa; só o sucesso troca para a tela final.
 */

import { Alert, Checkbox } from "@mui/material";
import { useState } from "react";

import { cpfMask } from "utils/cpf";

import { submitExtensionResponse } from "../api";
import { formatTotalText, PriceBreakdown } from "../components/PriceBreakdown";
import { StepActions } from "../components/StepActions";
import { useExtensionForm } from "../context";
import { formatIsoDate, formatPhone } from "../helpers";
import {
  Caption,
  Divider,
  Header,
  Panel,
  Paragraph,
  Row,
  SectionLabel,
  Stack,
} from "../styled";
import { COLORS } from "../tokens";
import { PAYMENT_METHOD_LABEL } from "../types";

const REMOVED_STYLE = {
  textDecoration: "line-through",
  color: COLORS.inkDisabled,
} as const;

export function Step5Review() {
  const { organizationId, token, data, state, updateState, goBack, setOutcome } =
    useExtensionForm();
  const { holder, dependents, eligiblePlans, offer } = data;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();

  const chosenPlan = eligiblePlans.find(
    (plan) => plan.id === state.chosenPlanId
  );

  const retainedCount = state.retainedDependentTaxPayerIds.length;

  async function handleSubmit() {
    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await submitExtensionResponse(organizationId, token, state);
      setOutcome("CONFIRMED");
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Não foi possível enviar sua confirmação. Tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Stack $gap="24px">
      <Stack $gap="4px">
        <Header>Confirmar e Enviar</Header>
        <Paragraph>Revise o resumo antes de confirmar sua extensão</Paragraph>
      </Stack>

      <Panel>
        <Stack $gap="8px">
          <SectionLabel>Titular</SectionLabel>
          <Paragraph>{holder.name}</Paragraph>
          <Caption>CPF: {cpfMask(holder.taxPayerId)}</Caption>
          <Caption>Nascimento: {formatIsoDate(holder.birthDate)}</Caption>
          <Caption>E-mail: {state.contact.personalEmail}</Caption>
          <Caption>Telefone: {formatPhone(state.contact.mobilePhone)}</Caption>
        </Stack>
      </Panel>

      <Panel>
        <Stack $gap="12px">
          <SectionLabel>Dependentes</SectionLabel>
          <Caption>
            {retainedCount} de {dependents.length}{" "}
            {dependents.length === 1
              ? "dependente incluído"
              : "dependentes incluídos"}
          </Caption>

          {dependents.length === 0 && (
            <Caption>Nenhum dependente vinculado.</Caption>
          )}

          {dependents.map((dependent, index) => {
            const edit = state.dependentEdits[dependent.taxPayerId];
            const isRetained = state.retainedDependentTaxPayerIds.includes(
              dependent.taxPayerId
            );

            return (
              <Paragraph
                key={`${index}-${dependent.taxPayerId}`}
                style={isRetained ? undefined : REMOVED_STYLE}
              >
                {edit?.name ?? dependent.name} ·{" "}
                {edit?.relationship ?? dependent.relationship}
                {!isRetained && " (removido)"}
              </Paragraph>
            );
          })}
        </Stack>
      </Panel>

      <Panel>
        <Stack $gap="12px">
          <SectionLabel>Plano Selecionado</SectionLabel>
          {chosenPlan ? (
            <>
              <Paragraph>{chosenPlan.name}</Paragraph>
              <Caption>
                {chosenPlan.code} · {offer.coverageMonths} meses de vigência
              </Caption>

              <Divider />

              <Row $justify="space-between">
                <Caption>Pagamento:</Caption>
                <Caption>{PAYMENT_METHOD_LABEL}</Caption>
              </Row>

              <Divider />

              <PriceBreakdown
                plan={chosenPlan}
                holder={holder}
                dependents={dependents}
                retainedDependentTaxPayerIds={
                  state.retainedDependentTaxPayerIds
                }
              />

              <Row $justify="space-between">
                <Caption>Total mensal</Caption>
                <SectionLabel>
                  {formatTotalText(
                    chosenPlan,
                    state.retainedDependentTaxPayerIds
                  )}
                </SectionLabel>
              </Row>
              <Caption>
                Valor aproximado. O valor final é definido pela operadora.
              </Caption>
            </>
          ) : (
            <Caption>Nenhum plano selecionado.</Caption>
          )}
        </Stack>
      </Panel>

      <Panel>
        <Stack $gap="8px">
          <SectionLabel>
            Documentos anexados ({state.uploadedDocuments.length})
          </SectionLabel>
          {state.uploadedDocuments.map((document) => (
            <Caption key={document.s3Key}>{document.fileName}</Caption>
          ))}
        </Stack>
      </Panel>

      <Alert severity="info">
        Após confirmar, a operadora será notificada em até 5 dias úteis.
      </Alert>

      <Row $gap="0">
        <Checkbox
          size="small"
          checked={state.hasAcceptedRn561}
          disabled={isSubmitting}
          onChange={() =>
            updateState({ hasAcceptedRn561: !state.hasAcceptedRn561 })
          }
        />
        <Paragraph>
          Declaro que li e concordo com os termos de extensão do plano conforme
          a RN 561 da ANS.
        </Paragraph>
      </Row>

      {!!submitError && <Alert severity="error">{submitError}</Alert>}

      <StepActions
        nextLabel="Confirmar Extensão e Enviar Documentos"
        onNext={handleSubmit}
        onBack={goBack}
        isNextDisabled={!state.hasAcceptedRn561 || !chosenPlan}
        isBusy={isSubmitting}
      />
    </Stack>
  );
}
